import BackButton from "@dashboard/components/BackButton";
import { ConfirmButton } from "@dashboard/components/ConfirmButton";
import { DashboardModal } from "@dashboard/components/Modal";
import { ProductMediaFragment } from "@dashboard/graphql";
import useModalDialogOpen from "@dashboard/hooks/useModalDialogOpen";
import { buttonMessages } from "@dashboard/intl";
import { Box, Checkbox, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { messages } from "./messages";

export interface MediaVariant {
  id: string;
  name: string;
  sku?: string | null;
}

interface ProductMediaVariantsDialogProps {
  media?: ProductMediaFragment | null;
  variants: MediaVariant[];
  /** Variant ids the media is currently assigned to. */
  assignedVariantIds: string[];
  open: boolean;
  confirmButtonState?: "default" | "loading" | "success" | "error";
  onClose: () => void;
  onConfirm: (variantIds: string[]) => void;
}

const ProductMediaVariantsDialog = (props: ProductMediaVariantsDialogProps) => {
  const { media, variants, assignedVariantIds, open, confirmButtonState, onClose, onConfirm } =
    props;
  const intl = useIntl();
  const [selectedVariants, setSelectedVariants] = useState<string[]>(assignedVariantIds);

  useModalDialogOpen(open, {
    onOpen: () => setSelectedVariants(assignedVariantIds),
    onClose: () => setSelectedVariants(assignedVariantIds),
  });

  const toggleVariant = (id: string) => {
    setSelectedVariants(current =>
      current.includes(id) ? current.filter(variantId => variantId !== id) : [...current, id],
    );
  };

  const parsedOembedData = media?.oembedData ? JSON.parse(media.oembedData) : null;
  const mediaUrl = parsedOembedData?.thumbnail_url || media?.url;

  const handleConfirm = () => onConfirm(selectedVariants);

  return (
    <DashboardModal open={open} onChange={onClose}>
      <DashboardModal.Content size="sm" __gridTemplateRows="auto auto 1fr auto">
        <DashboardModal.Header>
          <FormattedMessage {...messages.title} />
        </DashboardModal.Header>

        <Box display="flex" gap={4} alignItems="center">
          {mediaUrl && (
            <Box
              __width={64}
              __height={64}
              borderWidth={1}
              borderStyle="solid"
              borderColor="default1"
              borderRadius={2}
              overflow="hidden"
              flexShrink="0"
            >
              <Box
                as="img"
                src={mediaUrl}
                alt={media?.alt ?? ""}
                width="100%"
                height="100%"
                objectFit="contain"
              />
            </Box>
          )}
          <Text size={2} color="default2">
            <FormattedMessage {...messages.description} />
          </Text>
        </Box>

        <Box overflowY="auto" display="flex" flexDirection="column" gap={1} width="100%">
          {variants.length === 0 ? (
            <Text size={2} color="default2">
              <FormattedMessage {...messages.noVariants} />
            </Text>
          ) : (
            variants.map(variant => (
              <Box
                key={variant.id}
                as="label"
                display="flex"
                alignItems="center"
                gap={3}
                paddingX={2}
                paddingY={1.5}
                borderRadius={2}
                cursor="pointer"
                backgroundColor={{ hover: "default1Hovered" }}
                data-test-id="variant-assignment-row"
              >
                <Checkbox
                  checked={selectedVariants.includes(variant.id)}
                  onCheckedChange={() => toggleVariant(variant.id)}
                />
                <Box display="flex" flexDirection="column">
                  <Text size={3}>{variant.name || variant.sku || variant.id}</Text>
                  {variant.sku && variant.name && (
                    <Text size={1} color="default2">
                      {variant.sku}
                    </Text>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>

        <DashboardModal.Actions>
          <BackButton onClick={onClose} />
          <ConfirmButton
            transitionState={confirmButtonState ?? "default"}
            onClick={handleConfirm}
            data-test-id="submit"
          >
            {intl.formatMessage(buttonMessages.save)}
          </ConfirmButton>
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

ProductMediaVariantsDialog.displayName = "ProductMediaVariantsDialog";
export default ProductMediaVariantsDialog;
